<template>
  <OmniSearchInput
    v-model:search="search"
    v-model:ignoreCanceled="ignoreCanceled"
    v-model:ignoreCompleted="ignoreCompleted"
    :results="results"
    :loading="loading"
    @close="emit('close')"
    @openJob="openJob"
    @clearSearch="clearSearch"
    @loadMore="loadMore"
  />
</template>

<script setup lang="ts">
import OmniSearchInput from '@shared/components/OmniSearchInput.vue';
import {
  JobRoutesV1Repository,
  type JobSearchResponseType
} from '@shared/generated/';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const emit = defineEmits(['close']);
const router = useRouter();

const search = ref('');
const loading = ref(false);
const ignoreCanceled = ref(false);
const ignoreCompleted = ref(false);
const results = ref<JobSearchResponseType[]>();
const currentPage = ref(1);
const perPage = 10;
const lastPageLoaded = ref(false);
let inputUpdatedTimer: ReturnType<typeof setTimeout> | null = null;

const loadJobs = async (loadMore = false) => {
  loading.value = true;
  try {
    if (
      !search.value ||
      search.value.length < 3 ||
      (loadMore && lastPageLoaded.value)
    ) {
      return;
    }

    if (!loadMore) {
      currentPage.value = 1;
    }

    const params: {
      ignoreCompleted?: boolean;
      ignoreCanceled?: boolean;
      searchToken?: string;
      pageNumber?: number;
      perPage?: number;
    } = {};

    params.pageNumber = currentPage.value;
    params.perPage = perPage;

    if (ignoreCompleted.value) {
      params.ignoreCompleted = true;
    }

    if (ignoreCanceled.value) {
      params.ignoreCanceled = true;
    }

    if (search.value) {
      params.searchToken = search.value;
    }

    const paginatedResponse =
      await JobRoutesV1Repository.jobRoutesV1JobSearchInputs(params);
    if (loadMore) {
      results.value = [...(results.value || []), ...paginatedResponse.data];
    } else {
      results.value = paginatedResponse.data;
    }
    lastPageLoaded.value = currentPage.value >= paginatedResponse.total_pages;
  } finally {
    loading.value = false;
  }
};

const openJob = (jobId: string) => {
  router.push({ name: 'JobDetail', params: { jobId } });
  emit('close');
};

const clearSearch = () => {
  search.value = '';
  // results.value will be automatically set to undefined by the search watcher
};

const loadMore = async () => {
  currentPage.value += 1;
  await loadJobs(true);
};

// Watch for filter changes and reload jobs
watch(ignoreCanceled, async () => {
  await loadJobs();
});

watch(ignoreCompleted, async () => {
  await loadJobs();
});

watch(search, async () => {
  if (inputUpdatedTimer !== null) {
    clearTimeout(inputUpdatedTimer);
  }

  inputUpdatedTimer = setTimeout(async () => {
    await loadJobs();
  }, 500);
});
</script>
